import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type CampaignTableSortDirection,
  type CampaignTableSortKey,
  sortCampaignTableCampaigns,
} from "@/components/campaign/campaign-table-sort";
import { fetchCampaignAnalytics, fetchDashboardSummary } from "@/lib/dashboard-api";
import { useAuthStore } from "@/stores/auth";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";
import { type PolicyFilter, type RunFilter, runGroup } from "./campaign-list-filters";

const campaignsRoute = getRouteApi("/_authed/campaigns/");

/**
 * Campaign list data for the analytics scope: the summary + analytics queries,
 * the client-side filters/sort, and pagination kept in the URL.
 */
export function useCampaignList({
  scopeShopIds,
  smartCreationOpen,
}: {
  scopeShopIds: string[];
  smartCreationOpen: boolean;
}) {
  const { t } = useTranslation("campaign");
  const analyticsSearch = campaignsRoute.useSearch();
  const navigate = campaignsRoute.useNavigate();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const delivery = analyticsSearch.delivery;
  const page = analyticsSearch.page;
  const pageSize = analyticsSearch.pageSize;
  const [search, setSearch] = useState("");
  const [analyticsSearchTerm, setAnalyticsSearchTerm] = useState("");
  const [policyFilters, setPolicyFilters] = useState<PolicyFilter[]>([]);
  const [runFilters, setRunFilters] = useState<RunFilter[]>([]);
  const [managedGroupFilters, setManagedGroupFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<CampaignTableSortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<CampaignTableSortDirection>("desc");
  const scopeKey = scopeShopIds.join(",");
  const previousScopeKey = useRef(scopeKey);
  const query = useQuery({
    queryKey: ["dashboard", "summary", scopeKey, range, timeZone],
    queryFn: () => fetchDashboardSummary(range, timeZone, scopeShopIds),
    enabled: !smartCreationOpen && (scopeShopIds.length > 0 || Boolean(currentShopId)),
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => setAnalyticsSearchTerm(search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  // AC: switching the shop scope must not leave stale scope-bound state —
  // reset pagination when the selected set changes (filters here are
  // scope-independent: delivery/search/policy/run keep their meaning).
  useEffect(() => {
    if (previousScopeKey.current !== scopeKey) {
      previousScopeKey.current = scopeKey;
      void navigate({ search: (previous) => ({ ...previous, page: 1 }), replace: true });
    }
  }, [navigate, scopeKey]);

  const analyticsQuery = useQuery({
    queryKey: [
      "campaigns",
      "analytics",
      scopeKey,
      range,
      timeZone,
      analyticsSearch.granularity,
      delivery,
      analyticsSearchTerm,
      policyFilters,
      runFilters,
    ],
    queryFn: ({ signal }) =>
      fetchCampaignAnalytics(
        {
          range,
          timeZone,
          granularity: analyticsSearch.granularity,
          delivery,
          search: analyticsSearchTerm,
          policy: policyFilters,
          run: runFilters,
          shopIds: scopeShopIds,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    enabled: !smartCreationOpen,
  });

  const filtered = useMemo(() => {
    if (!query.data) return [];
    const term = search.trim().toLowerCase();
    return query.data.campaigns.filter((campaign) => {
      if (delivery !== "all" && campaign.status !== delivery) return false;
      if (term && !campaign.name.toLowerCase().includes(term)) return false;
      if (
        policyFilters.length > 0 &&
        !policyFilters.includes(campaign.aiPolicyState as PolicyFilter)
      )
        return false;
      if (runFilters.length > 0 && !runFilters.includes(runGroup(campaign))) return false;
      if (
        managedGroupFilters.length > 0 &&
        !managedGroupFilters.includes(campaign.managedGroup?.id ?? "__ungrouped")
      )
        return false;
      return true;
    });
  }, [delivery, managedGroupFilters, policyFilters, query.data, runFilters, search]);
  const managedGroupOptions = useMemo(() => {
    const groups = new Map<string, string>();
    for (const campaign of query.data?.campaigns ?? []) {
      if (campaign.managedGroup) groups.set(campaign.managedGroup.id, campaign.managedGroup.name);
    }
    return [
      ...Array.from(groups, ([value, label]) => ({ value, label })),
      { value: "__ungrouped", label: t("list.filters.ungrouped") },
    ];
  }, [query.data?.campaigns, t]);
  const sortedCampaigns = useMemo(
    () => sortCampaignTableCampaigns(filtered, sortKey, sortDirection),
    [filtered, sortDirection, sortKey],
  );
  const pageCount = Math.max(1, Math.ceil(sortedCampaigns.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = sortedCampaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const resetPage = () => {
    void navigate({ search: (previous) => ({ ...previous, page: 1 }), replace: true });
  };
  const handleSort = (nextKey: CampaignTableSortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(nextKey);
      setSortDirection("desc");
    }
    resetPage();
  };

  useEffect(() => {
    if (query.data && page !== currentPage) {
      void navigate({
        search: (previous) => ({ ...previous, page: currentPage }),
        replace: true,
      });
    }
  }, [currentPage, navigate, page, query.data]);

  return {
    query,
    analyticsQuery,
    search,
    analyticsSearchTerm,
    delivery,
    pageSize,
    currentPage,
    pageCount,
    pageItems,
    filteredCount: filtered.length,
    policyFilters,
    runFilters,
    managedGroupFilters,
    managedGroupOptions,
    sortKey,
    sortDirection,
    handleSort,
    changeSearch: (value: string) => {
      setSearch(value);
      resetPage();
    },
    changePolicyFilters: (next: PolicyFilter[]) => {
      setPolicyFilters(next);
      resetPage();
    },
    removePolicyFilter: (value: PolicyFilter) => {
      setPolicyFilters((current) => current.filter((item) => item !== value));
    },
    changeRunFilters: (next: RunFilter[]) => {
      setRunFilters(next);
      resetPage();
    },
    changeManagedGroupFilters: (next: string[]) => {
      setManagedGroupFilters(next);
      resetPage();
    },
  };
}

export type CampaignList = ReturnType<typeof useCampaignList>;
